import BaseElement from "./BaseElement";
import urlHelpers from "../../../../services/helpers/url";
import {normalizePadding, wrapCellAngleHtml} from "@frontend/group/modules/simple-table-builder/helpers";
import {CELL_ANGLE_STYLE_RULE} from "@frontend/group/modules/simple-table-builder/constants";
import {normalizeSymbolFontText} from "@frontend/group/modules/simple-table-builder/symbol-font";

const CACHE_IMAGE_DATA_URL = new Map(); // url -> Promise<base64>

export default class TableCell extends BaseElement {
  valueHtml = "";
  value = "";

  src = "";
  link = "";
  // Name this cell is mapped by on import. A table's columns are mapped through the header row;
  // a cell with a name of its own is filled individually, which is what a spec sheet needs — its
  // values are not a repeating list, they each come from their own feed column.
  mappingName = "";
  colspan = 1;
  rowspan = 1;
  // Static cells only: which static row this cell belongs to (see Table.getStaticRowGroups).
  staticRowIndex = null;
  // Static cells only: the body row this static row is anchored under (-1 = under the header row).
  // Null keeps it in the band above or below the table.
  afterRowIndex = null;
  isHeader = false;
  paddingOverride = null;
  dotLeaderEnabled = false;

  setPadding(padding) {
    this.paddingOverride = normalizePadding(padding);
    this.applyPadding(this.paddingOverride);
  }

  hasCustomPadding() {
    return !!this.paddingOverride;
  }

  applyPadding(padding) {
    if (!padding) return;

    this.styles.setRules({
      "padding-top": `${padding.top}px`,
      "padding-right": `${padding.right}px`,
      "padding-bottom": `${padding.bottom}px`,
      "padding-left": `${padding.left}px`,
    });
  }

  /**
   * @param {string} value
   * @param {string} link
   * @param {{ format?: 'text'|'html' }} opts
   */
  constructor(value = "", link = "", opts = {}) {
    super();

    this.styles.setRules({
      "border-width": "1px",
      "border-color": "#000000",
      "border-style": "solid",
      "padding-top": "2px",
      "padding-bottom": "2px",
      "padding-left": "5px",
      "padding-right": "5px",
      "background-color": "#ffffff",
      "white-space": "pre-line",
      "line-height": 1.5,
    });

    this.link = link;

    this.setValue(value);
  }

  /**
   * @param {string} value
   */
  setValue(value = "") {
    const format = this.isHtml(value) ? "html" : "text";
    const raw = normalizeSymbolFontText(String(value ?? ""));

    if (format === "html") {
      const safeHtml = this.sanitizeHtml(raw);
      this.valueHtml = safeHtml;
      this.value = this.htmlToText(safeHtml).trim(); // <-- text into value
    } else {
      this.value = raw;                       // <-- text into value
      this.valueHtml = raw;
    }
  }

  /**
   * Use this for mapping instead of valueHtml/valueRaw.
   * Optionally normalize spaces/nbsp/etc if you want stricter matching.
   */
  getMappingValue() {
    return this.normalizeTextForMapping(this.value);
  }

  setSrc(src = "") {
    this.src = src;
  }

  setLink(link) {
    this.link = link;
  }

  setMappingName(mappingName = "") {
    this.mappingName = String(mappingName ?? "").trim();
  }

  getMappingName() {
    return this.mappingName;
  }

  setColspan(value) {
    this.colspan = value ?? this.colspan;
  }

  setRowspan(value) {
    this.rowspan = value ?? this.rowspan;
  }

  setDotLeaderEnabled(value = false) {
    this.dotLeaderEnabled = !!value;
  }

  getDotLeaderEnabled() {
    return this.dotLeaderEnabled;
  }

  getRowspanAttribute() {
    return this.rowspan > 1 ? `rowspan="${this.rowspan}"` : "";
  }

  getColspanAttribute() {
    return `colspan="${this.colspan}"`;
  }

  getSrc() {
    return this.src;
  }

  getLink() {
    return this.link;
  }

  getStyles() {
    return this.styles.getStyles();
  }

  async imageUrlToBase64(url) {
    const _url = urlHelpers.normalizeImgUrl(url);

    let promise = CACHE_IMAGE_DATA_URL.get(_url);

    if (!promise) {
      promise = (async () => {
        const data = await fetch(_url);
        const blob = await data.blob();

        return new Promise((resolve, reject) => {
          const reader = new FileReader();

          reader.readAsDataURL(blob);

          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
        });
      })();

      promise.catch(() => CACHE_IMAGE_DATA_URL.delete(_url));
      CACHE_IMAGE_DATA_URL.set(_url, promise);
    }

    return promise;
  }

  /**
   * The table is rasterised through an SVG loaded as an image, which cannot fetch external
   * resources, so images inside the cell HTML are inlined as base64 first.
   */
  async inlineImages(html) {
    if (!/<img/i.test(html)) {
      return html;
    }

    const doc = this.parseHtml(html);
    for (const img of doc.querySelectorAll("img")) {
      img.style.maxWidth = "100%";
      // on the baseline the line box also holds the font's descent, which differs per renderer
      img.style.verticalAlign = "bottom";
      const src = img.getAttribute("src") || "";
      if (!src || src.startsWith("data:")) continue;
      try {
        img.setAttribute("src", await this.imageUrlToBase64(src));
      } catch (e) {
        console.log("An error occurred while loading a table cell image.", e);
      }
    }

    return doc.body.innerHTML;
  }

  /**
   * @param {boolean} tableColumnMode
   * @return {Promise<string>}
   */
  async toHTML(tableColumnMode = false, truncateLongText = false) {
    const truncationStyles = truncateLongText
      ? 'white-space:nowrap;overflow:hidden;text-overflow:ellipsis;'
      : '';

    if (this.src) {
      const base64 = await this.imageUrlToBase64(this.src);
      return `<td style="${this.styles.getStyle(tableColumnMode)}${truncationStyles}" ${this.getColspanAttribute()} ${this.getRowspanAttribute()}><img style="width: 100%; display: block" src="${base64}" alt="img"/></td>`;
    }

    const contentHtml = this.anchorLineBox(await this.inlineImages(this.getContentHtml()));
    const innerHtml = this.shouldRenderDotLeader()
      ? this.getDotLeaderHtml(contentHtml)
      : contentHtml;
    const angle = parseFloat(this.styles.getRule(CELL_ANGLE_STYLE_RULE)) || 0;
    const angledHtml = wrapCellAngleHtml(innerHtml, angle, this.styles.getStyles());
    // Containing block for the out-of-flow rotated text.
    const angleStyles = angle ? 'position:relative;' : '';
    const linkAttribute = this.link ? ` data-link="${this.link.replace(/"/g, '&quot;')}"` : '';

    return `<td style="${this.styles.getStyle(tableColumnMode)}${truncationStyles}${angleStyles}" ${this.getColspanAttribute()} ${this.getRowspanAttribute()}${linkAttribute}>${angledHtml}</td>`;
  }

  /**
   * @param {boolean} tableColumnMode
   * @return {{id, stringStyles: string, styles: Styles, value: string, valueHtml: string, valueText: string, src: string, link: string, colspan: number, rowspan: number}}
   */
  toData(tableColumnMode = false) {
    return {
      id: this.id,
      stringStyles: this.styles.getStyle(tableColumnMode),
      styles: this.styles,
      value: this.value,
      valueHtml: this.valueHtml,
      src: this.src,
      link: this.link,
      colspan: this.colspan,
      rowspan: this.rowspan,
      paddingOverride: this.paddingOverride,
      dotLeaderEnabled: this.dotLeaderEnabled,
      ...(this.mappingName ? { mappingName: this.mappingName } : {}),
    };
  }

  // ----------------------------
  // Helpers
  // ----------------------------

  /**
   * Escape HTML special chars (plain text -> safe HTML).
   * @param {string} s
   */
  escapeHtml(s) {
    return String(s)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  /**
   * Prefer DOMPurify if available (recommended).
   * Fallback sanitizer is intentionally conservative.
   *
   * @param {string} html
   */
  sanitizeHtml(html) {
    // If DOMPurify is present in the app, use it.
    // window.DOMPurify is a common way it’s exposed.
    if (typeof window !== "undefined" && window.DOMPurify?.sanitize) {
      return window.DOMPurify.sanitize(html, {
        // You can tune this allowlist.
        ALLOWED_TAGS: [
          "b", "strong", "i", "em", "u",
          "br", "p", "div", "span",
          "ul", "ol", "li",
          "sup", "sub",
          "mark", "small", "img",
        ],
        ALLOWED_ATTR: ["style", "src", "width", "height", "alt"], // if you really need styles; otherwise remove it
      });
    }

    // Fallback: strip script/style and risky attributes.
    // NOTE: This is not as strong as DOMPurify, but better than raw HTML injection.
    const doc = this.parseHtml(html);

    // Remove script/style
    doc.querySelectorAll("script, style").forEach((n) => n.remove());

    // Remove event handlers and javascript: URLs
    doc.querySelectorAll("*").forEach((el) => {
      [...el.attributes].forEach((attr) => {
        const name = attr.name.toLowerCase();
        const value = String(attr.value || "");
        if (name.startsWith("on")) el.removeAttribute(attr.name);
        if (name === "href" || name === "src") {
          if (/^\s*javascript:/i.test(value)) el.removeAttribute(attr.name);
        }
      });
    });

    // Allow only safe tags by replacing other tags with their text content
    const allowed = new Set([
      "B", "STRONG", "I", "EM", "U",
      "BR", "P", "DIV", "SPAN",
      "UL", "OL", "LI",
      "SUP", "SUB",
      "MARK", "SMALL", "IMG",
    ]);

    const all = Array.from(doc.body.querySelectorAll("*"));
    for (const el of all) {
      if (!allowed.has(el.tagName)) {
        const text = doc.createTextNode(el.textContent || "");
        el.replaceWith(text);
      }
    }

    return doc.body.innerHTML;
  }

  /**
   * Convert HTML to plain text, preserving line breaks for br/p/div/li.
   * @param {string} html
   */
  htmlToText(html) {
    const doc = this.parseHtml(html);

    // Insert newlines after block-ish elements so textContent keeps structure.
    doc.querySelectorAll("br").forEach((br) => br.replaceWith(doc.createTextNode("\n")));
    doc.querySelectorAll("p, div, li").forEach((el) => {
      // Ensure newline at end of block
      el.appendChild(doc.createTextNode("\n"));
    });

    const text = doc.body.textContent || "";
    return text.replace(/\n{3,}/g, "\n\n").trimEnd();
  }

  /**
   * Normalize mapping text: convert nbsp, collapse repeated spaces, trim.
   * Tune this depending on how strict your matching should be.
   */
  normalizeTextForMapping(text) {
    return String(text || "")
      .replace(/\u00A0/g, " ")     // nbsp -> space
      .replace(/[ \t]+/g, " ")    // collapse spaces/tabs
      .replace(/\s+\n/g, "\n")    // remove trailing spaces before newline
      .trim();
  }

  /**
   * Sanitize href used in <a href="...">.
   * Allows http(s), mailto, tel, and relative URLs. Everything else -> "#".
   */
  sanitizeHref(href) {
    const raw = String(href || "").trim();
    if (!raw) return "#";
    if (/^\s*javascript:/i.test(raw)) return "#";
    if (/^(https?:|mailto:|tel:|\/|\.\/|\.\.\/)/i.test(raw)) return raw;
    return "#";
  }

  /**
   * Parse HTML safely into a document.
   * @param {string} html
   */
  parseHtml(html) {
    const parser = new DOMParser();
    return parser.parseFromString(String(html || ""), "text/html");
  }

  isHtml(input) {
    const s = String(input || '').trim();
    if (!s) return false;

    // Fast check: contains a tag-like pattern
    if (!/<[a-z][\s\S]*>/i.test(s)) return false;

    // More strict: parse and see if there are element nodes
    const doc = new DOMParser().parseFromString(s, 'text/html');
    return Array.from(doc.body.childNodes).some(n => n.nodeType === 1);
  }

  /**
   * A cell whose text all sits inside a smaller-font tag has no text of its own, and headless
   * Chrome — the export renderer — then drops the cell's own line box, making the row shorter
   * there than in the browser, which keeps it. A word joiner in the cell's own font keeps that
   * line box in both: zero width, no break opportunity, invisible. Skipped for an empty cell,
   * where it would create a line box that neither engine has today.
   */
  anchorLineBox(html) {
    if (!this.valueHtml) {
      return html;
    }

    // Before a block child the joiner would get a line of its own, so it goes inside the block.
    const leadingBlockTags = html.match(/^(?:\s*<a\b[^>]*>)?(?:\s*<(?:div|p|ul|ol|li)\b[^>]*>)+/i)?.[0] || '';

    return `${leadingBlockTags}&#8288;${html.slice(leadingBlockTags.length)}`;
  }

  getContentHtml() {
    if (!this.link) {
      return this.valueHtml;
    }

    const safeHref = this.sanitizeHref(this.link);
    return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer">${this.valueHtml}</a>`;
  }

  shouldRenderDotLeader() {
    if (!this.dotLeaderEnabled || this.src) {
      return false;
    }

    const alignment = this.getHorizontalAlignment();
    if (!['left', 'right'].includes(alignment)) {
      return false;
    }

    return this.htmlToText(this.valueHtml).trim() !== '';
  }

  getHorizontalAlignment() {
    return String(this.styles.getRule('text-align') || 'left').toLowerCase();
  }

  getFontSizePx() {
    const raw = this.styles.getRule('font-size');
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 16;
  }

  getLeaderGapPx() {
    return Math.max(1, Math.round(this.getFontSizePx() * 0.08));
  }

  getPaddingPx(side) {
    const raw = this.styles.getRule(`padding-${side}`);
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }

  getLeaderStyle() {
    const fontSize = this.getFontSizePx();
    const dotSize = Math.max(1, Math.round(fontSize * 0.09));
    const dotStep = Math.max(3, Math.round(fontSize * 0.3));
    const offsetY = Math.max(3, Math.round(fontSize * 0.38));
    const parts = [
      'display:block',
      'width:100%',
      'min-width:0',
      'overflow:hidden',
      `height:${dotSize + 2}px`,
      'align-self:flex-end',
      `transform:translateY(-${offsetY}px)`,
      'background-repeat:space no-repeat',
      `background-size:${dotStep}px ${dotSize + 2}px`,
      `background-image:radial-gradient(circle, currentColor ${dotSize}px, transparent ${dotSize + 0.2}px)`,
      `background-position:left center`,
    ];

    return `${parts.join(';')};`;
  }

  getDotLeaderHtml(contentHtml) {
    const align = this.getHorizontalAlignment();
    const gap = this.getLeaderGapPx();
    const paddingLeft = this.getPaddingPx('left');
    const paddingRight = this.getPaddingPx('right');
    const leader = `<div aria-hidden="true" style="${this.getLeaderStyle()}"></div>`;
    const wrapperStyle = 'display:flex; align-items:flex-end; width:100%; min-width:0;';
    const contentStyle = `flex:0 1 auto; min-width:0; white-space:pre-line; text-align:${align};`;
    const leaderWrapStyle = 'flex:1 1 auto; min-width:0;';

    if (align === 'right') {
      return [
        `<div style="${wrapperStyle}">`,
        `<div style="${leaderWrapStyle} margin-left:-${paddingLeft}px; padding-right:${gap}px;">${leader}</div>`,
        `<div style="${contentStyle}">${contentHtml}</div>`,
        '</div>',
      ].join('');
    }

    return [
      `<div style="${wrapperStyle}">`,
      `<div style="${contentStyle} padding-right:${gap}px;">${contentHtml}</div>`,
      `<div style="${leaderWrapStyle} margin-right:-${paddingRight}px;">${leader}</div>`,
      '</div>',
    ].join('');
  }
}
